import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

export interface CodeInputProps {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  hasError?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}

export interface CodeInputRef {
  focus: () => void;
  focusFirst: () => void;
  clear: () => void;
}

export const CodeInput = forwardRef<CodeInputRef, CodeInputProps>(
  ({ length = 6, value = '', onChange, onComplete, hasError = false, disabled = false, autoFocus = false }, ref) => {
    const [digits, setDigits] = useState<string[]>(() => {
      const initial = value.split('').slice(0, length);
      return [...initial, ...Array(length - initial.length).fill('')];
    });

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Sync external value changes
    useEffect(() => {
      const newDigits = value.split('').slice(0, length);
      setDigits([...newDigits, ...Array(length - newDigits.length).fill('')]);
    }, [value, length]);

    // Auto focus first input
    useEffect(() => {
      if (autoFocus && inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, [autoFocus]);

    useImperativeHandle(ref, () => ({
      focus: () => {
        const firstEmpty = digits.findIndex(d => d === '');
        const indexToFocus = firstEmpty === -1 ? length - 1 : firstEmpty;
        inputRefs.current[indexToFocus]?.focus();
      },
      focusFirst: () => {
        inputRefs.current[0]?.focus();
      },
      clear: () => {
        setDigits(Array(length).fill(''));
        onChange?.('');
        inputRefs.current[0]?.focus();
      },
    }));

    const updateDigits = useCallback(
      (newDigits: string[]) => {
        setDigits(newDigits);
        const newValue = newDigits.join('');
        onChange?.(newValue);
        if (newValue.length === length && !newDigits.includes('')) {
          onComplete?.(newValue);
        }
      },
      [length, onChange, onComplete],
    );

    const handleChange = (index: number, inputValue: string) => {
      if (disabled) return;

      // Handle paste of full code
      if (inputValue.length > 1) {
        const pastedDigits = inputValue.replace(/\D/g, '').split('').slice(0, length);
        const newDigits = [...Array(length).fill('')];
        pastedDigits.forEach((digit, i) => {
          newDigits[i] = digit;
        });
        updateDigits(newDigits);
        const nextIndex = Math.min(pastedDigits.length, length - 1);
        inputRefs.current[nextIndex]?.focus();
        return;
      }

      // Single digit input
      const digit = inputValue.replace(/\D/g, '');
      if (digit.length === 0) return;

      const newDigits = [...digits];
      newDigits[index] = digit;
      updateDigits(newDigits);

      // Move to next input
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      if (e.key === 'Backspace') {
        e.preventDefault();
        const newDigits = [...digits];

        if (digits[index]) {
          // Clear current digit
          newDigits[index] = '';
          updateDigits(newDigits);
        } else if (index > 0) {
          // Move to previous and clear
          newDigits[index - 1] = '';
          updateDigits(newDigits);
          inputRefs.current[index - 1]?.focus();
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        inputRefs.current[index - 1]?.focus();
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        e.preventDefault();
        inputRefs.current[index + 1]?.focus();
      } else if (e.key === 'Delete') {
        e.preventDefault();
        const newDigits = [...digits];
        newDigits[index] = '';
        updateDigits(newDigits);
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      if (disabled) return;

      const pastedData = e.clipboardData.getData('text');
      const pastedDigits = pastedData.replace(/\D/g, '').split('').slice(0, length);

      if (pastedDigits.length > 0) {
        const newDigits = [...Array(length).fill('')];
        pastedDigits.forEach((digit, i) => {
          newDigits[i] = digit;
        });
        updateDigits(newDigits);
        const nextIndex = Math.min(pastedDigits.length, length - 1);
        inputRefs.current[nextIndex]?.focus();
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    };

    const cssClasses = [
      'form-input',
      'form-input--code-input',
      hasError && 'form-input--error',
      disabled && 'form-input--disabled',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={cssClasses}>
        <div className="form-input__input-container">
          <div className="form-input__code-boxes">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={el => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={length}
                value={digit}
                onChange={e => handleChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                onPaste={handlePaste}
                onFocus={handleFocus}
                disabled={disabled}
                aria-label={`Digit ${index + 1} of ${length}`}
                autoComplete="one-time-code"
                className="form-input__code-box"
              />
            ))}
          </div>
        </div>
      </div>
    );
  },
);

CodeInput.displayName = 'CodeInput';
